import React, { useContext, useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import axiosInstance from "../axiosConfig";
import { AuthContext } from "../AuthContext";
import "./WorkerAdvanceManagement.css";
const PAGE_SIZE = 10;
const money = (v) =>
  `Rs. ${Number(v || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
const iso = (d) => new Date(d).toISOString().slice(0, 10);
const accountsRoles = ["admin", "superadmin", "owner", "accounts"];
const pageRows = (rows, page) =>
  rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
function Pagination({ page, total, setPage }) {
  const pages = Math.ceil(total / PAGE_SIZE);
  if (pages <= 1) return null;
  return (
    <div className="advance-pagination">
      <span>
        Page {page} of {pages} <small>({total} records)</small>
      </span>
      <div>
        <button disabled={page === 1} onClick={() => setPage(page - 1)}>
          ‹ Previous
        </button>
        <button disabled={page === pages} onClick={() => setPage(page + 1)}>
          Next ›
        </button>
      </div>
    </div>
  );
}
export default function WorkerAdvanceManagement() {
  const { user } = useContext(AuthContext);
  const now = new Date();
  const [fromDate, setFromDate] = useState(
    iso(new Date(now.getFullYear(), now.getMonth(), 1)),
  );
  const [toDate, setToDate] = useState(iso(now));
  const [workers, setWorkers] = useState([]);
  const [queue, setQueue] = useState([]);
  const [amounts, setAmounts] = useState({});
  const [reasons, setReasons] = useState({});
  const [loading, setLoading] = useState(false);
  const [sidebar, setSidebar] = useState(true);
  const [workerSearch, setWorkerSearch] = useState("");
  const [workerFilter, setWorkerFilter] = useState("all");
  const [workerPage, setWorkerPage] = useState(1);
  const [queueSearch, setQueueSearch] = useState("");
  const [queueStatus, setQueueStatus] = useState("all");
  const [queuePage, setQueuePage] = useState(1);
  const actor = {
    actorId: user?.EmployeeID,
    actorName: user?.Name,
    actorRole: user?.Auth,
    actorDepartment: user?.Department,
  };
  const canPay = accountsRoles.includes(String(user?.Auth || "").toLowerCase());
  const loadWorkers = async () => {
    setLoading(true);
    try {
      const r = await axiosInstance.get(
        "/api/worker-advance-management/workers",
        {
          params: {
            fromDate,
            toDate,
            ...actor,
          },
        },
      );
      setWorkers(r.data.workers || []);
      setAmounts({});
      setReasons({});
      setWorkerPage(1);
    } catch (e) {
      Swal.fire(
        "Error",
        e.response?.data?.error || "Could not calculate advances.",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };
  const loadQueue = async () => {
    if (!canPay) return;
    try {
      const r = await axiosInstance.get(
        "/api/worker-advance-management/accounts/requests",
        {
          params: actor,
        },
      );
      setQueue(r.data || []);
      setQueuePage(1);
    } catch (e) {
      Swal.fire(
        "Error",
        e.response?.data?.error || "Could not load requests.",
        "error",
      );
    }
  };
  useEffect(() => {
    loadWorkers();
  }, []);
  useEffect(() => {
    loadQueue();
  }, [user?.Auth]);
  useEffect(() => {
    document.body.classList.toggle("worker-advance-loading", loading);
    return () => document.body.classList.remove("worker-advance-loading");
  }, [loading]);
  const visibleWorkers = useMemo(
    () =>
      workers
        .filter((w) => {
          const matches = `${w.WorkerCode} ${w.WorkerName}`
            .toLowerCase()
            .includes(workerSearch.toLowerCase());
          const eligible = Number(w.AcceptableAdvance) > 0;
          return (
            matches &&
            (workerFilter === "all" ||
              (workerFilter === "eligible" && eligible) ||
              (workerFilter === "balance-missing" && !eligible))
          );
        })
        .sort(
          (a, b) =>
            String(b.WorkerCode || "").localeCompare(
              String(a.WorkerCode || ""),
              undefined,
              {
                numeric: true,
              },
            ),
        ),
    [workers, workerSearch, workerFilter],
  );
  const visibleQueue = useMemo(
    () =>
      queue
        .filter(
          (r) =>
            `${r.RequestId} ${r.WorkerCode} ${r.WorkerName} ${r.SubmittedBy || ""}`
              .toLowerCase()
              .includes(queueSearch.toLowerCase()) &&
            (queueStatus === "all" || r.Status === queueStatus),
        )
        .sort(
          (a, b) =>
            (({
              Submitted: 0,
              Paid: 1,
              Rejected: 2,
            })[a.Status] ?? 3) -
              ({
              Submitted: 0,
              Paid: 1,
              Rejected: 2,
              }[b.Status] ?? 3) ||
            new Date(b.SubmittedDate || 0) - new Date(a.SubmittedDate || 0),
        ),
    [queue, queueSearch, queueStatus],
  );
  const totals = useMemo(
    () => ({
      wages: workers.reduce((s, w) => s + Number(w.TotalWages || 0), 0),
      balance: workers.reduce(
        (s, w) => s + Number(w.PendingAdvanceBalance || 0),
        0,
      ),
      allowed: workers.reduce(
        (s, w) => s + Number(w.AcceptableAdvance || 0),
        0,
      ),
    }),
    [workers],
  );
  const submit = async (row) => {
    const requestedAmount = Number(amounts[row.WorkerCode] || 0);
    const reason = reasons[row.WorkerCode] || "";
    if (!requestedAmount || requestedAmount > Number(row.AcceptableAdvance))
      return Swal.fire(
        "Invalid amount",
        `Maximum allowed is ${money(row.AcceptableAdvance)}.`,
        "warning",
      );
    try {
      setLoading(true);
      await axiosInstance.post("/api/worker-advance-management/requests", {
        workerCode: row.WorkerCode,
        fromDate,
        toDate,
        requestedAmount,
        reason,
        ...actor,
      });
      Swal.fire(
        "Submitted",
        "Advance request submitted successfully.",
        "success",
      );
      setAmounts({
        ...amounts,
        [row.WorkerCode]: "",
      });
      setReasons({
        ...reasons,
        [row.WorkerCode]: "",
      });
      loadQueue();
    } catch (e) {
      Swal.fire(
        "Not submitted",
        e.response?.data?.error || "Try again.",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };
  const pay = async (row) => {
    const r = await Swal.fire({
      title: "Mark advance as paid?",
      text: `${row.WorkerName} - ${money(row.RequestedAmount)}`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, mark paid",
    });
    if (!r.isConfirmed) return;
    try {
      await axiosInstance.post(
        `/api/worker-advance-management/accounts/${row.RequestId}/pay`,
        {
          ...actor,
          paidAmount: row.RequestedAmount,
        },
      );
      Swal.fire(
        "Updated",
        "Advance payment and live balance updated.",
        "success",
      );
      loadQueue();
      loadWorkers();
    } catch (e) {
      Swal.fire(
        "Failed",
        e.response?.data?.error || "Could not post payment.",
        "error",
      );
    }
  };
  const sendAdvanceWhatsApp = async (row) => {
    const r = await Swal.fire({
      title: "Send advance payment message?",
      text: `${row.WorkerName} - ${money(row.RequestedAmount)}`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Send WhatsApp",
    });
    if (!r.isConfirmed) return;
    try {
      await axiosInstance.post(
        `/api/worker-advance-management/accounts/${row.RequestId}/send-whatsapp`,
        actor,
      );
      Swal.fire("Sent", "Advance payment WhatsApp sent.", "success");
    } catch (e) {
      Swal.fire(
        "Not sent",
        e.response?.data?.error || "Could not send WhatsApp.",
        "error",
      );
    }
  };
  const reject = async (row) => {
    const r = await Swal.fire({
      title: "Reject request",
      input: "text",
      inputPlaceholder: "Reason",
      showCancelButton: true,
      preConfirm: (v) => !v && Swal.showValidationMessage("Reason is required"),
    });
    if (!r.isConfirmed) return;
    try {
      await axiosInstance.post(
        `/api/worker-advance-management/accounts/${row.RequestId}/reject`,
        {
          ...actor,
          reason: r.value,
        },
      );
      loadQueue();
    } catch (e) {
      Swal.fire(
        "Failed",
        e.response?.data?.error || "Could not reject.",
        "error",
      );
    }
  };
  return (
    <div className="d-flex dashboard">
      <div className={sidebar ? "sidebar-container" : "sidebar-hidden"}>
        <Sidebar />
      </div>
      <div className="flex-grow-1">
        <Header
          toggleSidebar={() => setSidebar(!sidebar)}
          isSidebarVisible={sidebar}
        />
        <main className="main-container advance-page">
          <a href="/ai-form-assistant?form=worker-advance" className="btn btn-outline-primary mb-3">AI se advance request banayein</a>
          <section className="advance-hero">
            <div>
              <span className="advance-eyebrow">FINANCE OPERATIONS</span>
              <h2>Worker Advance Management</h2>
              <p>
                Calculate eligibility, submit requests, and track payments in
                one place.
              </p>
            </div>
            <button
              className="advance-refresh"
              onClick={loadWorkers}
              disabled={loading}
            >
              <i className="bi bi-arrow-clockwise" /> Refresh
            </button>
          </section>
          <section className="advance-stat-grid">
            <article>
              <span>Workers in range</span>
              <strong>{workers.length}</strong>
              <small>for selected dates</small>
            </article>
            <article>
              <span>Total wages</span>
              <strong>{money(totals.wages)}</strong>
              <small>calculated payroll amount</small>
            </article>
            <article>
              <span>Advance balance</span>
              <strong>{money(totals.balance)}</strong>
              <small>current outstanding</small>
            </article>
            <article>
              <span>Maximum allowed</span>
              <strong>{money(totals.allowed)}</strong>
              <small>available to request</small>
            </article>
          </section>
          <section className="advance-panel">
            <div className="advance-panel-title">
              <div>
                <h4>Calculate advance eligibility</h4>
                <p>Select a date range and narrow the worker list.</p>
              </div>
            </div>
            <div className="advance-filters">
              <label>
                From date
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                />
              </label>
              <label>
                To date
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                />
              </label>
              <label className="filter-grow">
                Search worker
                <input
                  placeholder="Search by code or name"
                  value={workerSearch}
                  onChange={(e) => {
                    setWorkerSearch(e.target.value);
                    setWorkerPage(1);
                  }}
                />
              </label>
              <label>
                Show
                <select
                  value={workerFilter}
                  onChange={(e) => {
                    setWorkerFilter(e.target.value);
                    setWorkerPage(1);
                  }}
                >
                  <option value="all">All workers</option>
                  <option value="eligible">Eligible only</option>
                  <option value="balance-missing">No eligible amount</option>
                </select>
              </label>
              <button
                className="advance-primary"
                disabled={loading}
                onClick={loadWorkers}
              >
                {loading ? "Calculating..." : "Calculate"}
              </button>
            </div>
          </section>
          <section className="advance-table-card">
            <div className="advance-table-heading">
              <div>
                <h4>Worker eligibility</h4>
                <p>{visibleWorkers.length} matching workers</p>
              </div>
              <span className="advance-chip">Allowed = 75% of payable</span>
            </div>
            <div className="table-responsive">
              <table className="table advance-table mb-0">
                <thead>
                  <tr>
                    <th>Worker</th>
                    <th>Wages</th>
                    <th>Advance balance</th>
                    <th>Payable</th>
                    <th>Allowed</th>
                    <th>Request amount</th>
                    <th>Reason</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {pageRows(visibleWorkers, workerPage).map((w) => (
                    <tr key={w.WorkerCode}>
                      <td>
                        <div className="worker-name">{w.WorkerName}</div>
                        <div className="worker-code">{w.WorkerCode}</div>
                      </td>
                      <td>{money(w.TotalWages)}</td>
                      <td>{money(w.PendingAdvanceBalance)}</td>
                      <td>{money(w.TotalPayable)}</td>
                      <td className="allowed-value">
                        {money(w.AcceptableAdvance)}
                      </td>
                      <td>
                        <input
                          className="advance-amount"
                          type="number"
                          min="0"
                          max={w.AcceptableAdvance}
                          disabled={!w.AcceptableAdvance}
                          value={amounts[w.WorkerCode] || ""}
                          onChange={(e) =>
                            setAmounts({
                              ...amounts,
                              [w.WorkerCode]: e.target.value,
                            })
                          }
                          placeholder="Enter amount"
                        />
                      </td>
                      <td>
                        <input
                          className="advance-reason form-control"
                          type="text"
                          disabled={!w.AcceptableAdvance}
                          value={reasons[w.WorkerCode] || ""}
                          onChange={(e) =>
                            setReasons({
                              ...reasons,
                              [w.WorkerCode]: e.target.value,
                            })
                          }
                          placeholder="Reason"
                        />
                      </td>
                      <td>
                        <button
                          className="advance-submit"
                          disabled={loading || !w.AcceptableAdvance}
                          onClick={() => submit(w)}
                        >
                          Submit
                        </button>
                      </td>
                    </tr>
                  ))}
                  {!loading && !visibleWorkers.length && (
                    <tr>
                      <td colSpan="8" className="advance-empty">
                        No workers match these filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <Pagination
              page={workerPage}
              total={visibleWorkers.length}
              setPage={setWorkerPage}
            />
          </section>
          {canPay && (
            <section className="advance-table-card accounts-card">
              <div className="advance-table-heading">
                <div>
                      <h4>Accounts requests</h4>
                      <p>Review submitted advances and update payment status.</p>
                </div>
                <button className="advance-refresh small" onClick={loadQueue}>
                  Refresh list
                </button>
              </div>
              <div className="advance-filters accounts-filters">
                <label className="filter-grow">
                  Search requests
                  <input
                    placeholder="Request no., worker or submitted by"
                    value={queueSearch}
                    onChange={(e) => {
                      setQueueSearch(e.target.value);
                      setQueuePage(1);
                    }}
                  />
                </label>
                <label>
                  Status
                  <select
                    value={queueStatus}
                    onChange={(e) => {
                      setQueueStatus(e.target.value);
                      setQueuePage(1);
                    }}
                  >
                    <option value="all">All statuses</option>
                    <option value="Submitted">Submitted</option>
                    <option value="Paid">Paid</option>
                    <option value="Rejected">Rejected</option>
                  </select>
                </label>
                <span className="request-count">
                  {visibleQueue.length} results
                </span>
              </div>
              <div className="table-responsive">
                <table className="table advance-table mb-0">
                  <thead>
                    <tr>
                      <th>Request</th>
                      <th>Worker</th>
                      <th>Submitted</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows(visibleQueue, queuePage).map((r) => (
                      <tr key={r.RequestId}>
                        <td className="request-id">#{r.RequestId}</td>
                        <td>
                          <div className="worker-name">{r.WorkerName}</div>
                          <div className="worker-code">{r.WorkerCode}</div>
                        </td>
                        <td>{r.SubmittedDate ? iso(r.SubmittedDate) : "-"}</td>
                        <td className="allowed-value">
                          {money(r.RequestedAmount)}
                        </td>
                        <td>
                          <span
                            className={`advance-status ${r.Status.toLowerCase()}`}
                          >
                            {r.Status}
                          </span>
                        </td>
                        <td>
                          {r.Status === "Submitted" && (
                            <div className="action-buttons">
                              <button
                                className="advance-pay"
                                onClick={() => pay(r)}
                              >
                                Mark paid
                              </button>
                              <button
                                className="advance-reject"
                                onClick={() => reject(r)}
                              >
                                Reject
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                    {!visibleQueue.length && (
                      <tr>
                        <td colSpan="6" className="advance-empty">
                          No requests match these filters.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <Pagination
                page={queuePage}
                total={visibleQueue.length}
                setPage={setQueuePage}
              />
            </section>
          )}
        </main>
      </div>
    </div>
  );
}
